import '../styles/main.css'
import { Figtree, Inter, Open_Sans } from 'next/font/google'
import localFont from 'next/font/local'
import type { AppProps } from 'next/app'
import { useEffect } from 'react'
// import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { HeroUIProvider } from '@nextui-org/react-latest'
import { appWithTranslation, useTranslation } from 'next-i18next'
import nextI18NextConfig from '../next-i18next.config'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { setDayjsLocale } from '@utils/setDayjsLocale'
import ErrorBoundary from '@components/atoms/ErrorBoundary'
import { ToastContainer } from 'react-toastify'

import Metrics from 'metrics'
// const ReactQueryDevtools =
//   process.env.NODE_ENV === 'development'
//     ? dynamic(
//         () =>
//           import('@tanstack/react-query-devtools').then(
//             (mod) => mod.ReactQueryDevtools
//           ),
//         { ssr: false }
//       )
//     : null

// Contexts
import { AuthProvider } from '@contexts/Auth'
// const AuthProvider = dynamic(() =>
//   import('@contexts/Auth').then((mod) => mod.AuthProvider)
// )
// const ErrorBoundary = dynamic(() => import('@components/atoms/ErrorBoundary'))

import { CategoriesProvider } from '@contexts/categoriesContext'

// Import Styles
import 'react-toastify/dist/ReactToastify.css'
import '../styles/override.scss'
import '../styles/custom-toast.css'

import { cacheStorage } from '@utils/storage'
import Head from 'next/head'
import TopLoader from '@components/atoms/TopLoader'
import { CartAndWishlistProvider } from '@contexts/CartAndWishList'
import { NotificationsProvider } from '@contexts/Notifications'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
      retry: false,
      gcTime: 1000 * 60 * 60 * 12, // 12 hours
    },
  },
})

const persister = createAsyncStoragePersister({
  storage: typeof window !== 'undefined' ? cacheStorage : null,
})

const nordiquePro = localFont({
  src: '../public/static/fonts/Nordique-Pro-Bold.ttf',
  variable: '--font-nordique-pro',
  display: 'swap',
  preload: false,
})

const sofiaPro = localFont({
  src: [
    {
      path: '../public/static/fonts/SofiaPro/SofiaProExtraLight.woff2',
      weight: '200',
      style: 'normal',
    },
    {
      path: '../public/static/fonts/SofiaPro/SofiaProLight.woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: '../public/static/fonts/SofiaPro/SofiaProRegular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: '../public/static/fonts/SofiaPro/SofiaProMedium.woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: '../public/static/fonts/SofiaPro/SofiaProSemiBold.woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: '../public/static/fonts/SofiaPro/SofiaProBold.woff2',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../public/static/fonts/SofiaPro/SofiaProBlack.woff2',
      weight: '900',
      style: 'normal',
    },
  ],
  variable: '--font-sofia-pro',
  display: 'swap',
  preload: false,
})

const figtreeRegular = Figtree({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700', '800'],
  // weight: '400',
  variable: '--font-figtree',
  display: 'swap',
  adjustFontFallback: true,
  preload: true,
})

const openSans = Open_Sans({
  subsets: ['latin'],
  weight: ['400', '600', '700'],
  style: ['normal'],
  variable: '--font-open-sans',
  preload: false,
})

const inter = Inter({
  subsets: ['latin'],
  weight: ['400', '500', '700'],
  style: ['normal'],
  variable: '--font-inter',
  preload: false,
})

const persistedQueries: string[] = [
  'user-wishlist',
  'study-course',
  'user-cart',
  'saved-items',
  'search-summary',
  'referral-user',
  'my-courses',
  'notifications',
  'author-statistics',
  'unified-auth',
  'referrals-stats',
  'os-percentages',
  'country-percentages',
  'referral-earnings',
  'referrals-top-courses',
  'referrals-graph-data',
  'course-lecture-content',
  'categories',
]

const MyApp = ({ Component, pageProps, router: initialRouter }: AppProps) => {
  const router = useRouter()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (!router.isReady) return
    const pageClass = router.pathname
      .substring(1) // Remove leading slash
      .split('/') // Split into segments
      .filter((segment) => !/^\[.*\]$/.test(segment)) // Exclude dynamic segments
      .join('-') // Join remaining segments with hyphens
    if (pageClass) document.body.classList.add(pageClass)
    return () => {
      try {
        document.body.classList.remove(pageClass)
      } catch (error) {}
    }
  }, [router.pathname, router.isReady])

  useEffect(() => {
    if (!router.isReady) return
    const savedLang = localStorage.getItem('edugram-language') || 'en'

    if (router.locale !== savedLang) {
      router.replace(router.asPath, undefined, { locale: savedLang })
    }

    import('@configs/firebase').then(({ analytics }) => {
      analytics
    })
  }, [router.isReady])

  useEffect(() => {
    setDayjsLocale(i18n.language)
  }, [i18n.language])

  const { locale, asPath } = initialRouter

  const languages = ['en', 'es', 'it', 'de']
  const baseUrl = 'https://www.edugram.io'

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="theme-color" content="#ffffff" />
        <meta
          name="theme-color"
          content="#ffffff"
          media="(prefers-color-scheme: light)"
        />
        {/* Default Favicon - Used when no preference is set */}
        <link rel="icon" href="/favicon-light.ico" />

        {/* Favicon for Light Mode */}
        <link
          rel="icon"
          href="/favicon-light.ico"
          media="(prefers-color-scheme: light)"
        />

        {/* Favicon for Dark Mode */}
        <link
          rel="icon"
          href="/favicon-dark.ico"
          media="(prefers-color-scheme: dark)"
        />
        {/* DNS Prefetch */}
        <link rel="preconnect" href="https://firebase.googleapis.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link
          rel="dns-prefetch"
          href="https://identitytoolkit.googleapis.com"
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href={`${baseUrl}${locale === 'en' ? '' : `/${locale}`}${asPath}`}
        />

        {/* Hreflang for all locales */}
        {languages.map((lang) => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`${baseUrl}${lang === 'en' ? '' : `/${lang}`}${asPath}`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${baseUrl}${asPath}`}
        />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <main
        className={`${sofiaPro.variable} ${nordiquePro.variable} ${openSans.variable} ${inter.variable} ${figtreeRegular.variable}`}
        id="edu-main"
      >
        <ErrorBoundary>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              dehydrateOptions: {
                shouldDehydrateQuery: ({ queryKey }) => {
                  return persistedQueries?.includes(
                    (queryKey[0] || '') as string
                  )
                },
              },
            }}
          >
            <HeroUIProvider>
              <AuthProvider>
                <CartAndWishlistProvider>
                  <NotificationsProvider>
                    <ToastContainer
                      position="top-right"
                      autoClose={3000}
                      hideProgressBar={false}
                      closeButton={false}
                    />

                    <TopLoader />
                    <CategoriesProvider initial={pageProps?.categories}>
                      <Component {...pageProps} />
                    </CategoriesProvider>
                    <Metrics />
                  </NotificationsProvider>
                </CartAndWishlistProvider>
              </AuthProvider>
            </HeroUIProvider>

            {/* //     {ReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />} */}
          </PersistQueryClientProvider>
        </ErrorBoundary>
      </main>
    </>
  )
}

export default appWithTranslation(MyApp, nextI18NextConfig)
